import { uniqBy } from 'lodash-es';
import {
    isString,
    isNotNullable,
    isNullable,
    findMap,
    isObject,
} from '@bayer-int/portletce-shared';
import * as t from 'io-ts';

import type { Dimension, Hierarchy } from 'portlet/services';

import type { ViewId } from './view';
import type { FilterId } from './filters';
import type { SettingValueMap } from './settings';
import type { DateType} from './filterValue';
import { DateTypeC } from './filterValue';

/**
 * String in format of `settings.${SETTING_KEY}`, allows for dynamic referencing of setting values.
 * If the setting value changes (e.g. due to user input), the filter Id that the portlet listens to will change.
 */
type SettingReference = string;
type FilterIdOrSettingReference = FilterId | SettingReference;

/**
 * End user can declare that this filter item (element of listenTo) should be of particular FilterType.
 * When the filter is emitted as a result of user interaction, FilterHint may be used to enrich/add missing properties
 * to the filter event.
 */
const FilterHintC = t.partial({
    hierarchy: t.union([
        t.string,
        t.intersection([
            t.type({ id: t.string }),
            t.partial({ internalKeyKeywords: t.record(t.string, t.array(t.string)) }),
        ]),
    ]),
    dimension: t.union([
        t.string,
        t.intersection([t.type({ internalKey: t.string }), t.partial({})]),
    ]),
    date: t.union([DateTypeC, t.type({ type: DateTypeC })]),
    dateRange: t.union([DateTypeC, t.type({ type: DateTypeC })]),
    scale: t.type({}),
    smartViewTag: t.type({}),
    role: t.type({}),
});
/**
 * End user can declare filter column mapping. The common scenario is that one dataset column contains keys (identifiers) and another one text values (labels).
 * When user click on a value, the filter should contain the underlying key.
 * E.g. Clicking on `Germany` should emit `DE` filter.
 */
const ColumnMappingC = t.record(
    t.string,
    t.type({
        key: t.string,
        value: t.string,
    }),
);

const ListenToDefinitionC = t.union(
    [
        t.string,
        t.intersection([
            t.type({
                /** ['filter1'] and [{ item: 'filter1' }] are equivalent */
                item: t.union([
                    t.string,
                    t.type({
                        /** picks the first filterId that is not empty, useful when used with `SettingReference`s */
                        firstOf: t.array(t.string),
                    }),
                ]),
            }),
            FilterHintC,
            t.partial({
                /** if defined, changes the name of the signal that the filter will be converted to */
                as: t.string,
                interaction: t.union([
                    t.null,
                    t.partial({ keywords: t.array(t.string), columnMapping: ColumnMappingC }),
                ]),
            }),
        ]),
    ],
    'ListenToDefinition',
);

export type ListenToDefinition = t.TypeOf<typeof ListenToDefinitionC>;

export type ListenToDefinitions = Array<ListenToDefinition>;
export type ListenToDefinitionsPerView = Array<{
    // 'viewId' being null means that listenTo definition is present on main config
    viewId: ViewId | null;
    definition: ListenToDefinition;
}>;
export type ListenTo = FilterId[];
/** user defined string tags that help to match user interaction to specific listenTo item */
export type Keyword = string;
export type FilterIdToSignalMapping = Record<FilterId, string>;
export type ListenToWithMapping = { items: FilterId[]; signalMapping: FilterIdToSignalMapping };
export type ResolvedListenToDefinition = {
    item: FilterId;
    as: string;
    interactionEnabled: boolean;
    keywords: Keyword[] | null;
    defaultKeywords: Keyword[];
    filterHint: ResolvedFilterHint | null;
    columnMapping: Record<string, { key: string; value: string }>;
};
export type FilterHint = t.TypeOf<typeof FilterHintC>;
export type ResolvedListenToDefinitions = Array<ResolvedListenToDefinition>;
export type ResolvedFilterHint = Partial<{
    hierarchy: { id: Hierarchy['hierarchyId']; internalKeyKeywords?: Record<string, Keyword[]> };
    dimension: { internalKey: Dimension['internalKey'] };
    date: { type: DateType };
    dateRange: { type: DateType };
    scale: Record<string, never>;
    smartViewTag: Record<string, never>;
    role: Record<string, never>;
}>;

/**
 * `ListenTo` controlls which filters will be written to the portlet state and later used
 * when running the view (by converting filters to signals utilized by `aclib`).
 *
 * In `ListenTo` definition, user can directly list observed filters by name,
 * or calculate them based on current setting values (using the `setting.SETTING_NAME` syntax).
 * It is also possible control the output signal name which will be produced based on the filter.
 */
export const ListenTo = {
    codec: ListenToDefinitionC,

    /** Generates list of actual filterIds which the portlet listens to,
     * based on the definition and current settings */
    materializeListenTo: (
        definitions: ListenToDefinition[],
        settings: Partial<SettingValueMap>,
    ): ResolvedListenToDefinition[] => {
        const resolvedListenTos = definitions
            .flatMap(item => ListenTo.materializeListenToDefinition(item, settings))
            .filter(isNotNullable);

        return uniqBy(resolvedListenTos, ({ item }) => item);
    },

    materializeListenToDefinition: (
        definition: ListenToDefinition,
        settings: Partial<SettingValueMap>,
    ): ResolvedListenToDefinition[] => {
        const interactionEnabled = isString(definition) || definition.interaction !== null;
        const keywords =
            !isString(definition) && definition.interaction && definition.interaction.keywords
                ? definition.interaction.keywords
                : null;

        if (isString(definition)) {
            const items = ListenTo.resolveFilterId(definition, settings);
            return items.map(item => ({
                item,
                as: item,
                keywords: null,
                interactionEnabled,
                filterHint: null,
                defaultKeywords: [item],
                columnMapping: {},
            }));
        }

        const filterHint = ListenTo.resolveFilterHint(definition);

        if (isString(definition.item)) {
            const items = ListenTo.resolveFilterId(definition.item, settings);

            return items.map(item => ({
                item,
                as: definition.as ?? item,
                keywords,
                interactionEnabled,
                filterHint,
                defaultKeywords: [item],
                columnMapping: definition.interaction?.columnMapping || {},
            }));
        }

        const items = findMap(
            definition.item.firstOf,
            item => ListenTo.resolveFilterId(item, settings),
            items => items.length !== 0,
        );

        return (items || []).map(item => ({
            item,
            as: definition.as ?? item,
            keywords,
            interactionEnabled,
            filterHint,
            defaultKeywords: [item],
            columnMapping: definition.interaction?.columnMapping || {},
        }));
    },

    resolveFilterId: (
        item: FilterIdOrSettingReference,
        settings: Partial<SettingValueMap>,
    ): FilterId[] => {
        const settingReferenceMatch = item.match(/settings\.([\w]+)/);
        if (!settingReferenceMatch) return [item];

        const [_, settingReference] = settingReferenceMatch;
        const settingValue = settings[settingReference];

        if (isNullable(settingValue)) return [];
        if (Array.isArray(settingValue)) return settingValue.map(String);

        return [String(settingValue)];
    },

    toFilterNameMapping: (listenTo: ResolvedListenToDefinitions): FilterIdToSignalMapping => {
        return listenTo.reduce((acc, { item, as }) => {
            acc[item] = as;
            return acc;
        }, {} as FilterIdToSignalMapping);
    },

    resolveFilterHint: (hint: FilterHint): ResolvedFilterHint | null => {
        if (hint.dimension)
            return {
                dimension: isObject(hint.dimension)
                    ? hint.dimension
                    : { internalKey: hint.dimension },
            };

        if (hint.hierarchy)
            return {
                hierarchy: isObject(hint.hierarchy) ? hint.hierarchy : { id: hint.hierarchy },
            };

        if (hint.date)
            return {
                date: isObject(hint.date) ? hint.date : { type: hint.date },
            };

        if (hint.dateRange)
            return {
                dateRange: isObject(hint.dateRange) ? hint.dateRange : { type: hint.dateRange },
            };

        if (hint.scale) return { scale: hint.scale };
        if (hint.smartViewTag) return { smartViewTag: hint.smartViewTag };
        if (hint.role) return { role: hint.role };

        return null;
    },
};
